/**
 * Tema tercihi: açık / koyu / sistem. Bileşenlerin dışında, modül seviyesinde tutulur;
 * tercihi hem Header menüsü değiştiriyor hem de kabuk okuyor, iki ayrı `useState`
 * birbirinden habersiz kalırdı.
 */

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** `purgeAllSessions` yalnızca oturum anahtarlarını siler; tercih çıkışta kalır. */
const STORAGE_KEY = "sso-rd:theme";

const MODES: ThemeMode[] = ["light", "dark", "system"];

function readStored(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return MODES.indexOf(raw as ThemeMode) >= 0 ? (raw as ThemeMode) : "system";
  } catch {
    // Depolama kapalı olabilir (gizli sekme, kurumsal politika); tema uygulamayı düşürmemeli.
    return "system";
  }
}

let mode: ThemeMode = readStored();
const listeners = new Set<() => void>();

function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveTheme(current: ThemeMode): ResolvedTheme {
  if (current === "system") return systemPrefersDark() ? "dark" : "light";
  return current;
}

export function getThemeMode(): ThemeMode {
  return mode;
}

/** Kök öğeye yazar; açık temada öznitelik hiç bulunmaz, varsayılan odur. */
function apply(): void {
  const root = document.documentElement;

  if (resolveTheme(mode) === "dark") {
    root.setAttribute("data-sso-theme", "dark");
  } else {
    root.removeAttribute("data-sso-theme");
  }
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
  }
  apply();
  listeners.forEach((listener) => listener());
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Modül yüklenirken uygulanır: efekte bırakılsaydı ilk boyama açık temayla
// yapılır ve kullanıcı koyuya dönüşü beyaz bir parlama olarak görürdü.
apply();

// "Sistem" seçiliyken cihaz temasını canlı takip eder.
if (typeof window.matchMedia === "function") {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (mode !== "system") return;
    apply();
    listeners.forEach((listener) => listener());
  };

  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", onChange);
  } else if (typeof query.addListener === "function") {
    // Safari 13 ve altı.
    query.addListener(onChange);
  }
}
